import type { ScoringEvent } from "../scenario/types";

/**
 * Live deterministic detector (keyword/intent heuristic). Mirrors the role of
 * packages/shared/services/voice/engine/detector.py: a fast, explainable first
 * pass that flags scoring events from a finalized learner utterance. In the
 * Cyberfrog build this is backed up by the post-call LLM judge (judge.py).
 */
export function detectEvents(utterance: string, events: ScoringEvent[]): ScoringEvent[] {
  const text = normalize(utterance);
  const hits: ScoringEvent[] = [];
  for (const ev of events) {
    const hints = ev.hints ?? [];
    if (hints.some((h) => matchHint(text, normalize(h)))) {
      hits.push(ev);
    }
  }
  return dedupe(hits);
}

/** Lowercase, strip most punctuation, collapse whitespace. */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Multi-word hints match as a substring; single-word hints match on a word
 * boundary so short tokens like "no" don't fire inside "now"/"know"/"another".
 */
function matchHint(text: string, hint: string): boolean {
  if (!hint) return false;
  if (hint.includes(" ")) return text.includes(hint);
  const re = new RegExp(`(?:^|\\s)${escapeRegExp(hint)}(?:$|\\s)`);
  return re.test(text);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function dedupe(events: ScoringEvent[]): ScoringEvent[] {
  const seen = new Set<string>();
  return events.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
}

/** Generic "the caller is pushing back / declining" heuristic, independent of
 *  any one scenario's scoring hints — used by the live vishing stage tracker
 *  (`useVoiceCall.ts`) to decide when a playbook stage has been REFUSED
 *  (as opposed to satisfied or just an ambiguous/off-topic reply), so it
 *  knows when to count a refusal toward `persistence` and steer the model
 *  onto the next stage. Deliberately loose — a false positive just means a
 *  stage advances one refusal earlier than ideal, which is harmless; a false
 *  negative just means the model keeps gently re-asking, which is also fine. */
const REFUSAL_HINTS = [
  "no",
  "nope",
  "not going to",
  "won't",
  "wont",
  "can't",
  "cant",
  "don't want",
  "dont want",
  "rather not",
  "not comfortable",
  "not telling",
  "not going to tell",
  "not sure i should",
  "why do you need",
  "why should i",
  "i don't think so",
  "i dont think so",
  "i refuse",
  "not answering",
  "not going to share",
  "not gonna",
];
export function looksLikeRefusal(utterance: string): boolean {
  const text = normalize(utterance);
  if (!text) return false;
  return REFUSAL_HINTS.some((h) => matchHint(text, h));
}
